import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { createCatalogTransport as defaultCreateCatalogTransport } from "../catalog/catalogTransport";
import { ErrorAlert } from "../components/ErrorAlert";
import { Modal } from "../components/Modal";
import { buildGroupFrames, buildIndividualFrames } from "../editor/frameLayout";
import { FeedbackContact } from "../feedback/FeedbackContact";
import { GettingStartedChecklist } from "../guide/GettingStartedChecklist";
import { useGuideState } from "../guide/guideStorage";
import { classHandoutPath, classPath, useNavigate } from "../router";

import { CanvasesPanel } from "./class-detail/CanvasesPanel";
import { GalleryPanel } from "./class-detail/GalleryPanel";
import { SettingsPanel } from "./class-detail/SettingsPanel";
import { StudentsPanel } from "./class-detail/StudentsPanel";
import { TrashPanel } from "./class-detail/TrashPanel";

import type { ApiErrorCode } from "../api/client";
import type {
  CatalogTransport,
  CatalogTransportFactory,
} from "../catalog/catalogTransport";
import type { ClassTab } from "../router";
import type {
  CanvasRecord,
  ClassRecord,
  StudentAccount,
  TeacherClassGalleryList,
} from "@canvaschool/contracts";

type Loaded = {
  classRecord: ClassRecord;
  canvases: CanvasRecord[];
  trashed: CanvasRecord[];
  roster: StudentAccount[];
};

type Dialog =
  | {
      kind: "createCanvas";
      title: string;
      template: "INDIVIDUAL" | "GROUP" | "PRESENTATION" | "NONE";
    }
  | { kind: "trashCanvas"; canvas: CanvasRecord }
  | { kind: "duplicateCanvas"; canvas: CanvasRecord; name: string }
  | { kind: "renameClass"; name: string }
  | { kind: "archiveClass" }
  | { kind: "renameStudent"; student: StudentAccount; name: string }
  | { kind: "clearStudent"; student: StudentAccount }
  | { kind: "resetStudent"; student: StudentAccount };

const TABS: { id: ClassTab; label: string }[] = [
  { id: "canvases", label: "캔버스" },
  { id: "students", label: "학생 계정" },
  { id: "gallery", label: "갤러리" },
  { id: "trash", label: "휴지통" },
  { id: "settings", label: "학급 설정" },
];

export const ClassDetailPage = ({
  classId,
  tab: tabProp = "canvases",
  createCatalogTransport = defaultCreateCatalogTransport,
}: {
  classId: string;
  tab?: ClassTab;
  createCatalogTransport?: CatalogTransportFactory;
}) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const authRef = useRef(auth);
  const catalogRefreshSequenceRef = useRef(0);
  const transportRef = useRef<CatalogTransport | null>(null);
  authRef.current = auth;
  const { api } = auth;
  const isTeacher =
    auth.state.status === "authenticated" &&
    auth.state.session.principal.role === "TEACHER";
  const isStudent =
    auth.state.status === "authenticated" &&
    auth.state.session.principal.role === "STUDENT";
  const userId =
    auth.state.status === "authenticated"
      ? auth.state.session.principal.userId
      : "";
  const guide = useGuideState(userId);

  const [tab, setTab] = useState<ClassTab>(tabProp);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<ApiErrorCode | null>(null);
  const [actionError, setActionError] = useState<ApiErrorCode | null>(null);
  const [catalogError, setCatalogError] = useState<ApiErrorCode | null>(null);
  const [subscriptionError, setSubscriptionError] =
    useState<ApiErrorCode | null>(null);
  const [gallery, setGallery] = useState<TeacherClassGalleryList | null>(null);
  /** Posts new since the teacher last looked; kept while the page is open. */
  const [galleryNew, setGalleryNew] = useState(0);
  const [galleryBadge, setGalleryBadge] = useState(true);

  useEffect(() => {
    setTab(tabProp);
  }, [tabProp]);

  const load = useCallback(async () => {
    try {
      const [classRecord, canvases, trashed, roster] = await Promise.all([
        api.getClass(classId),
        api.listCanvases(classId),
        isTeacher
          ? api.listTrashedCanvases(classId)
          : Promise.resolve({ items: [], nextCursor: null }),
        isTeacher ? api.getRoster(classId) : Promise.resolve([]),
      ]);
      setLoaded({
        classRecord,
        canvases: canvases.items,
        trashed: trashed.items,
        roster,
      });
      setLoadError(null);
    } catch (failure) {
      setLoadError(auth.handleError(failure));
    }
    // `auth` changes identity on every session update; its handler is stable in effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, classId, isTeacher]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshGallery = useCallback(async () => {
    if (!isTeacher) {
      return;
    }
    try {
      setGallery(await api.listClassGalleryPosts(classId));
    } catch (failure) {
      const code = auth.handleError(failure);
      if (code !== "UNAUTHENTICATED" && code !== "ACCOUNT_DISABLED") {
        setCatalogError(code);
      }
    }
    // `auth` changes identity on every session update; its handler is stable in effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, classId, isTeacher]);

  // The list is read up front so the tab can show how many posts are new.
  const galleryLoadedRef = useRef(false);
  useEffect(() => {
    if (!isTeacher || galleryLoadedRef.current) {
      return;
    }
    galleryLoadedRef.current = true;
    void (async () => {
      try {
        const result = await api.listClassGalleryPosts(classId);
        setGallery(result);
        setGalleryNew((current) => Math.max(current, result.unseenCount));
      } catch {
        // The badge is a convenience; the tab loads the list again.
      }
    })();
  }, [api, classId, isTeacher]);

  // Opening the tab marks the list seen; the badge goes, the count stays.
  const galleryTabOpen = isTeacher && tab === "gallery";
  useEffect(() => {
    if (!galleryTabOpen) {
      return;
    }
    setGalleryBadge(false);
    void (async () => {
      try {
        const result = await api.listClassGalleryPosts(classId);
        setGallery(result);
        setGalleryNew((current) => Math.max(current, result.unseenCount));
        await api.markClassGallerySeen(classId);
      } catch (failure) {
        auth.handleError(failure);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, classId, galleryTabOpen]);

  const refreshCatalog = useCallback(async () => {
    const sequence = ++catalogRefreshSequenceRef.current;
    try {
      const [canvases, trashed] = await Promise.all([
        api.listCanvases(classId),
        isTeacher
          ? api.listTrashedCanvases(classId)
          : Promise.resolve({
              items: [] as CanvasRecord[],
              nextCursor: null,
            }),
      ]);
      if (sequence !== catalogRefreshSequenceRef.current) {
        return;
      }
      setLoaded((current) =>
        current
          ? {
              ...current,
              canvases: canvases.items,
              trashed: trashed.items,
            }
          : current,
      );
      setCatalogError(null);
    } catch (failure) {
      if (sequence !== catalogRefreshSequenceRef.current) {
        return;
      }
      const code = auth.handleError(failure);
      if (code === "UNAUTHENTICATED" || code === "ACCOUNT_DISABLED") {
        return;
      }
      setCatalogError(code);
    }
  }, [api, classId, isTeacher, auth]);

  const ready = loaded !== null && loadError === null;
  useEffect(() => {
    if (!ready) {
      return;
    }
    const transport = createCatalogTransport({
      classId,
      getCsrfToken: async () => {
        const current = authRef.current;
        if (current.state.status !== "authenticated") {
          return null;
        }
        return current.state.session.csrfToken;
      },
      events: {
        onJoined: () => {
          setSubscriptionError(null);
          void refreshCatalog();
        },
        onChanged: () => {
          void refreshCatalog();
        },
        onJoinFailed: (code) => {
          if (code === "UNAUTHENTICATED" || code === "ACCOUNT_DISABLED") {
            authRef.current.handleError(new ApiError(401, code));
            return;
          }
          setSubscriptionError(code);
        },
        onDisconnected: () => undefined,
        onAccessLost: (code) => {
          authRef.current.handleError(new ApiError(401, code));
          if (code !== "UNAUTHENTICATED" && code !== "ACCOUNT_DISABLED") {
            setSubscriptionError(code);
          }
        },
      },
    });
    transportRef.current = transport;
    transport.connect();
    return () => {
      transportRef.current = null;
      transport.close();
    };
    // The transport lifetime follows page readiness, not list refresh renders.
    // `authRef` supplies the latest auth state and handler to all callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, classId, createCatalogTransport]);

  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  /** One class action at a time, so a double click never repeats it. */
  const run = async (
    action: () => Promise<unknown>,
    reload: "catalog" | "all" | "gallery" = "all",
  ) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setActionError(null);
    try {
      await action();
      setDialog(null);
      if (reload === "catalog") {
        await refreshCatalog();
      } else if (reload === "gallery") {
        await refreshGallery();
      } else {
        await load();
      }
    } catch (failure) {
      setActionError(auth.handleError(failure));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const openCreateCanvas = () =>
    setDialog({ kind: "createCanvas", title: "", template: "INDIVIDUAL" });

  const selectTab = (next: ClassTab) => {
    setTab(next);
    navigate(classPath(classId, next));
  };

  if (loadError) {
    return (
      <>
        <ErrorAlert code={loadError} />
        <button
          type="button"
          onClick={() => {
            setLoadError(null);
            void load();
          }}
        >
          다시 시도
        </button>
      </>
    );
  }
  if (!loaded) {
    return <p aria-busy="true">불러오는 중…</p>;
  }
  const { classRecord, canvases, trashed, roster } = loaded;
  const active = classRecord.status === "ACTIVE";
  const canManage = isTeacher && active;
  const guideUserId = canManage ? userId : undefined;
  const catalogNotice = subscriptionError ?? catalogError;

  return (
    <div className="canvaschool-page">
      <div className="canvaschool-class-title-row">
        <h1 className={isTeacher ? undefined : "canvaschool-class-context"}>
          {classRecord.name}
        </h1>
        {!active && (
          <span className="canvaschool-status canvaschool-status--wait">
            보관됨
          </span>
        )}
      </div>
      {actionError && <ErrorAlert code={actionError} />}

      {isTeacher && (
        <div className="canvaschool-tabs" role="tablist" aria-label="학급 메뉴">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className="canvaschool-tab"
              role="tab"
              aria-selected={tab === item.id}
              onClick={() => selectTab(item.id)}
            >
              {item.label}
              {item.id === "gallery" && galleryBadge && galleryNew > 0 && (
                <span
                  className="canvaschool-count-badge"
                  aria-label={`새 작품 ${galleryNew}개`}
                >
                  {galleryNew}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {isTeacher && active && (
        <GettingStartedChecklist
          guide={guide}
          mode="class"
          done={{
            class: true,
            share: guide.state.rosterShared.includes(classId),
            login: roster.some((student) => student.lastLoginAt !== null),
            canvas: canvases.length > 0,
            lesson: guide.state.openedCanvas,
          }}
          onShortcut={(id) => {
            if (id === "canvas") {
              openCreateCanvas();
            } else if (id === "share" || id === "login") {
              selectTab("students");
            } else {
              selectTab("canvases");
            }
          }}
        />
      )}

      <fieldset className="canvaschool-fieldset" disabled={busy}>
        {(!isTeacher || tab === "canvases") && (
          <CanvasesPanel
            classId={classId}
            canvases={canvases}
            canManage={canManage}
            isTeacher={isTeacher}
            isStudent={isStudent}
            catalogNotice={catalogNotice}
            onRetryCatalog={() => {
              transportRef.current?.reconnect();
              void refreshCatalog();
            }}
            guideUserId={guideUserId}
            onOpen={isTeacher ? guide.markCanvasOpened : undefined}
            onCreate={openCreateCanvas}
            onRename={(canvas, title) =>
              void run(
                () => api.renameCanvas(classId, canvas.id, title),
                "catalog",
              )
            }
            onTrash={(canvas) => setDialog({ kind: "trashCanvas", canvas })}
            onDuplicate={(canvas) =>
              setDialog({
                kind: "duplicateCanvas",
                canvas,
                name: `${canvas.title} 사본`,
              })
            }
          />
        )}

        {isTeacher && tab === "students" && (
          <StudentsPanel
            roster={roster}
            canManage={canManage}
            csvUrl={api.rosterCsvUrl(classId)}
            handoutPath={classHandoutPath(classId, true)}
            guideUserId={guideUserId}
            onShared={() => guide.markRosterShared(classId)}
            onRename={(student) =>
              setDialog({
                kind: "renameStudent",
                student,
                name: student.displayName ?? "",
              })
            }
            onClearName={(student) =>
              setDialog({ kind: "clearStudent", student })
            }
            onToggle={(student) =>
              void run(() =>
                api.updateStudent(classId, student.studentNumber, {
                  enabled: !student.enabled,
                }),
              )
            }
            onResetPassword={(student) =>
              setDialog({ kind: "resetStudent", student })
            }
          />
        )}

        {isTeacher && tab === "gallery" && (
          <GalleryPanel
            gallery={gallery}
            newCount={galleryNew}
            canManage={isTeacher}
            onTakeDown={(item) =>
              void run(() => api.withdrawGalleryPost(item.id), "gallery")
            }
            onAllowAgain={(item) =>
              void run(() => api.allowGalleryPostAgain(item.id), "gallery")
            }
          />
        )}

        {isTeacher && tab === "trash" && (
          <TrashPanel
            trashed={trashed}
            canRestore={active}
            onRestore={(canvas) =>
              void run(async () => {
                await api.restoreCanvas(classId, canvas.id);
                selectTab("canvases");
              }, "catalog")
            }
          />
        )}

        {isTeacher && tab === "settings" && (
          <SettingsPanel
            classRecord={classRecord}
            guideUserId={guideUserId}
            onRename={() =>
              setDialog({ kind: "renameClass", name: classRecord.name })
            }
            onArchive={() => setDialog({ kind: "archiveClass" })}
            onReactivate={() => void run(() => api.reactivateClass(classId))}
            onSaveGroupCount={(groupCount) =>
              void run(() => api.setGroupCount(classId, groupCount))
            }
          />
        )}
      </fieldset>
      {(!isTeacher || tab === "canvases") && <FeedbackContact api={api} />}

      {dialog?.kind === "createCanvas" && (
        <Modal
          title="캔버스 만들기"
          formName="캔버스 만들기"
          primaryLabel="캔버스 만들기"
          className="canvaschool-modal--wide"
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() => {
            const title = dialog.title.trim();
            if (!title) {
              return;
            }
            void run(async () => {
              let scene: unknown[] | undefined;
              if (dialog.template === "INDIVIDUAL") {
                scene = buildIndividualFrames(
                  roster.filter((student) => student.enabled),
                );
              } else if (dialog.template === "GROUP") {
                scene = buildGroupFrames(classRecord.groupCount ?? 4);
              }
              // 발표 수업은 학생 캔버스를 서버가 만든다. 장면을 보내지 않는다.
              await api.createCanvas(classId, title, {
                template: dialog.template,
                scene,
              });
            }, "catalog");
          }}
        >
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-canvas-title"
              className="canvaschool-form-label"
            >
              캔버스 제목
            </label>
            <input
              id="canvaschool-canvas-title"
              value={dialog.title}
              onChange={(event) =>
                setDialog({ ...dialog, title: event.target.value })
              }
              placeholder="예: 우리 반 미술 수업, 1단원 마인드맵"
              required
              autoFocus
            />
          </div>

          <div className="canvaschool-form-group">
            <span className="canvaschool-form-label">템플릿 선택</span>
            <div className="canvaschool-template-list">
              <label
                className={`canvaschool-template-card ${
                  dialog.template === "INDIVIDUAL" ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  className="canvaschool-template-radio"
                  name="canvas-template"
                  value="INDIVIDUAL"
                  checked={dialog.template === "INDIVIDUAL"}
                  onChange={() =>
                    setDialog({ ...dialog, template: "INDIVIDUAL" })
                  }
                />
                <span className="canvaschool-template-head">
                  <span className="canvaschool-template-title">
                    개인형 (1인 1칸)
                  </span>
                  <span className="canvaschool-template-note">학생 수만큼</span>
                </span>
                <span className="canvaschool-template-desc">
                  사용 중인 학생 수만큼 번호별 개인 프레임이 자동 생성됩니다.
                </span>
              </label>

              <label
                className={`canvaschool-template-card ${
                  dialog.template === "GROUP" ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  className="canvaschool-template-radio"
                  name="canvas-template"
                  value="GROUP"
                  checked={dialog.template === "GROUP"}
                  onChange={() => setDialog({ ...dialog, template: "GROUP" })}
                />
                <span className="canvaschool-template-head">
                  <span className="canvaschool-template-title">
                    모둠형 (모둠별 1칸)
                  </span>
                  <span className="canvaschool-template-note">
                    {classRecord.groupCount ?? 4}개 모둠
                  </span>
                </span>
                <span className="canvaschool-template-desc">
                  모둠별로 함께 협동할 수 있는 프레임이 자동 생성됩니다.
                </span>
              </label>

              <label
                className={`canvaschool-template-card ${
                  dialog.template === "PRESENTATION" ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  className="canvaschool-template-radio"
                  name="canvas-template"
                  value="PRESENTATION"
                  checked={dialog.template === "PRESENTATION"}
                  onChange={() =>
                    setDialog({ ...dialog, template: "PRESENTATION" })
                  }
                />
                <span className="canvaschool-template-head">
                  <span className="canvaschool-template-title">발표 수업</span>
                  <span className="canvaschool-template-note">
                    학생마다 슬라이드
                  </span>
                </span>
                <span className="canvaschool-template-desc">
                  학생마다 자기 슬라이드 캔버스를 받아 발표를 준비합니다.
                </span>
              </label>

              <label
                className={`canvaschool-template-card ${
                  dialog.template === "NONE" ? "is-selected" : ""
                }`}
              >
                <input
                  type="radio"
                  className="canvaschool-template-radio"
                  name="canvas-template"
                  value="NONE"
                  checked={dialog.template === "NONE"}
                  onChange={() => setDialog({ ...dialog, template: "NONE" })}
                />
                <span className="canvaschool-template-head">
                  <span className="canvaschool-template-title">
                    일반 (빈 캔버스)
                  </span>
                  <span className="canvaschool-template-note">자유 그리기</span>
                </span>
                <span className="canvaschool-template-desc">
                  프레임 구분 없이 자유롭게 시작할 수 있는 백지 캔버스입니다.
                </span>
              </label>
            </div>
          </div>
          <p className="canvaschool-hint">
            만든 뒤에도 제목은 바꿀 수 있고, 같은 구성이 필요하면 복제하면
            됩니다.
          </p>
        </Modal>
      )}
      {dialog?.kind === "trashCanvas" && (
        <Modal
          title="캔버스 휴지통 이동"
          primaryLabel="휴지통으로 이동"
          danger
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() =>
            void run(
              () => api.trashCanvas(classId, dialog.canvas.id),
              "catalog",
            )
          }
        >
          <div className="canvaschool-modal-danger-box">
            <p>
              <strong>{dialog.canvas.title}</strong> 캔버스를 휴지통으로
              옮길까요?
            </p>
            <p className="canvaschool-hint">
              학생 목록에서 사라지고 실시간 편집이 종료됩니다. 30일 안에 복원할
              수 있습니다.
            </p>
          </div>
        </Modal>
      )}
      {dialog?.kind === "duplicateCanvas" && (
        <Modal
          title="캔버스 복제"
          formName="캔버스 복제"
          primaryLabel="복제"
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() => {
            if (dialog.name.trim()) {
              void run(
                () =>
                  api.duplicateCanvas(
                    classId,
                    dialog.canvas.id,
                    dialog.name.trim(),
                  ),
                "catalog",
              );
            }
          }}
        >
          <p className="canvaschool-modal-lead">
            <strong>{dialog.canvas.title}</strong>을 통째로 복제합니다. 학생
            작업을 포함한 모든 내용이 그대로 복사됩니다.
          </p>
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-canvas-duplicate-name"
              className="canvaschool-form-label"
            >
              새 캔버스 제목
            </label>
            <input
              id="canvaschool-canvas-duplicate-name"
              value={dialog.name}
              onChange={(event) =>
                setDialog({ ...dialog, name: event.target.value })
              }
              placeholder="새 캔버스 제목을 입력하세요"
              required
              autoFocus
            />
          </div>
        </Modal>
      )}
      {dialog?.kind === "renameClass" && (
        <Modal
          title="학급 이름 변경"
          formName="학급 이름 변경"
          primaryLabel="저장"
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() => {
            if (dialog.name.trim()) {
              void run(() => api.renameClass(classId, dialog.name));
            }
          }}
        >
          <p className="canvaschool-modal-lead">
            학급 이름을 바꾸면 학생과 교사 화면에 바로 반영됩니다.
          </p>
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-class-rename"
              className="canvaschool-form-label"
            >
              학급 이름
            </label>
            <input
              id="canvaschool-class-rename"
              value={dialog.name}
              onChange={(event) =>
                setDialog({ ...dialog, name: event.target.value })
              }
              placeholder="예: 3학년 1반"
              required
              autoFocus
            />
          </div>
        </Modal>
      )}
      {dialog?.kind === "archiveClass" && (
        <Modal
          title="학급 보관"
          primaryLabel="보관"
          danger
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() => void run(() => api.archiveClass(classId))}
        >
          <div className="canvaschool-modal-danger-box">
            <p>학급을 보관하면 학생 로그인과 캔버스 편집이 즉시 중지됩니다.</p>
            <p className="canvaschool-hint">
              교사는 읽기와 내보내기를 계속할 수 있으며, 설정 탭에서 언제든 다시
              활성화할 수 있습니다.
            </p>
          </div>
        </Modal>
      )}
      {dialog?.kind === "renameStudent" && (
        <Modal
          title="이름 수정"
          formName="이름 수정"
          primaryLabel="저장"
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() => {
            if (dialog.name.trim()) {
              void run(() =>
                api.updateStudent(classId, dialog.student.studentNumber, {
                  displayName: dialog.name.trim(),
                }),
              );
            }
          }}
        >
          <div className="canvaschool-modal-target">
            <strong>{dialog.student.studentNumber}번 학생</strong>
            <span className="canvaschool-prefix">{dialog.student.loginId}</span>
          </div>
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-student-name"
              className="canvaschool-form-label"
            >
              이름
            </label>
            <input
              id="canvaschool-student-name"
              value={dialog.name}
              onChange={(event) =>
                setDialog({ ...dialog, name: event.target.value })
              }
              placeholder="학생 이름을 입력하세요"
              aria-describedby="canvaschool-student-name-hint"
              required
              autoFocus
            />
            <p id="canvaschool-student-name-hint" className="canvaschool-hint">
              학생이 처음 로그인할 때 이름을 직접 입력하므로 미리 넣지 않아도
              됩니다. 잘못 입력한 이름을 고칠 때만 쓰세요.
            </p>
            <p className="canvaschool-hint">
              출석부 번호와 연동되며 학생 개인 칸 이름에도 표시됩니다.
            </p>
          </div>
        </Modal>
      )}
      {dialog?.kind === "clearStudent" && (
        <Modal
          title="이름 지우기"
          primaryLabel="지우기"
          danger
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() =>
            void run(() =>
              api.updateStudent(classId, dialog.student.studentNumber, {
                displayName: null,
              }),
            )
          }
        >
          <div className="canvaschool-modal-target">
            <strong>{dialog.student.studentNumber}번 학생</strong>
            <span className="canvaschool-prefix">{dialog.student.loginId}</span>
          </div>
          <div className="canvaschool-modal-danger-box">
            <p>
              {dialog.student.studentNumber}번 학생의 이름을 지우면 다음 로그인
              때 이름을 새로 입력하게 됩니다.
            </p>
            <p className="canvaschool-hint">등록된 이름을 지우시겠습니까?</p>
          </div>
        </Modal>
      )}
      {dialog?.kind === "resetStudent" && (
        <Modal
          title="비밀번호 초기화"
          primaryLabel="초기화"
          pending={busy}
          onCancel={() => setDialog(null)}
          onPrimary={() =>
            void run(() =>
              api.resetStudentPassword(classId, dialog.student.studentNumber),
            )
          }
        >
          <div className="canvaschool-modal-target">
            <strong>{dialog.student.studentNumber}번 학생</strong>
            <span className="canvaschool-prefix">{dialog.student.loginId}</span>
          </div>
          <div className="canvaschool-modal-info-box">
            <p>
              비밀번호가 초기 비밀번호 <strong>12345678</strong>로 되돌아갑니다.
            </p>
            <p className="canvaschool-hint">
              초기화 후 학생에게 변경된 초기 비밀번호를 안내해 주세요.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};
